import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import styles from "./style.scss";
import SendImage from "images/icon/send.svg";
import SendActiveImage from "images/icon/send_active.svg";
import Stop from "images/icon/send_stop.svg";
import { AiResearchType } from "models/AiResearch";
import { trackAction } from "utils/helper/analytics";

const isMacOS = () =>
  typeof window !== "undefined" && /Mac/i.test(window.navigator.platform);

interface Props {
  type: AiResearchType;
  maxLength?: number; // 最大文字数
  maxRows?: number; // 最大行数
  prefillText?: string;
  onSubmit: (text: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// チャットフォームコンポーネント
export const ChatForm: React.FC<Props> = ({
  type,
  maxLength = 500,
  maxRows = 7,
  prefillText,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isOverMaxLength = text.length > maxLength; // 最大文字数超過判定
  const isIncludeUrl = /\b(?:https?:\/\/|www\d{0,3}\.)/.test(text); // URL形式のテキストが含まれているか
  // バリデーションメッセージ
  const validateMessage = isOverMaxLength
    ? `質問は${maxLength}文字以内で入力してください`
    : isIncludeUrl
    ? "URLは検索対象外です。URLを含めずに入力してください"
    : null;

  // 送信ボタンの活性/非活性判定
  const isDisabled = (!isLoading && text.trim() === "") || !!validateMessage;

  // テキストの行数に応じて高さを再計算し、最大行数分でスクロールさせる
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;

    const lineHeight = 20; // 行の高さ（px）
    const maxHeight = lineHeight * maxRows; // 最大高さ（px）

    element.style.height = "auto"; // 一旦高さを自動調整(高さを確認のため)
    const nextHeight = Math.min(element.scrollHeight, maxHeight);
    element.style.height = `${nextHeight}px`;
    element.style.overflowY =
      element.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [text, maxRows]);

  useEffect(() => {
    if (prefillText === undefined) return;
    setText(prefillText);
  }, [prefillText]);

  const handleSubmit = () => {
    if (isOverMaxLength || isIncludeUrl || text.trim() === "") return;
    trackAction("clickSendAskAi");
    onSubmit(text);
    setText("");
  };

  // Mac: Cmd + Enter, Windows: Ctrl + Enterで送信
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") return;
    const isSubmitByShortcut =
      (isMacOS() && event.metaKey) || (!isMacOS() && event.ctrlKey);
    if (!isSubmitByShortcut) return;
    event.preventDefault();
    handleSubmit();
  };

  return (
    <div className={cn(styles.container, styles[type])}>
      <textarea
        ref={textareaRef}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="質問を入力してください"
        className={styles.textarea}
      />
      <div className={styles.footer}>
        {validateMessage && (
          <span className={styles.error}>{validateMessage}</span>
        )}
        <span className={styles.counter}>
          <span className={cn(styles.current, isOverMaxLength && styles.over)}>
            {text.length}
          </span>{" "}
          / {maxLength}
        </span>
        <button
          onClick={isLoading ? onCancel : handleSubmit}
          disabled={isDisabled}
          className={cn(styles.sendButton, isLoading && styles.loading)}
        >
          {isLoading ? (
            <>
              <img className={styles.stop} src={Stop} alt="stop" />
              <div className={styles.stopTooltip}>回答を停止</div>
            </>
          ) : (
            <img
              className={styles.sendActive}
              src={!isDisabled ? SendActiveImage : SendImage}
              alt="send"
            />
          )}
        </button>
      </div>
    </div>
  );
};
